import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { useAuth, useMissionRoleInterview, useVideoPresentation } from "hooks";
import { Header } from "components/DisplayBox";
import { H3, H4 } from "components/Text";
import DataTable from "components/DataTable";
import { CardList } from "components/Card";
import { DestructableLink, EditLink } from "components/Links";
import RoleInterviewQuestionForm from "./RoleInterviewQuestionForm";
import { Toggle, FileUpload } from "components/Form";
import { TOGGLE_SIZE } from "components/Form/Toggle";
import RoleInterviewTemplateForm from "./RoleInterviewTemplateForm";
import RoleInterviewQuestionCard from "./RoleInterviewQuestionCard";
import {
  RoleInterviewAIQuestionsModal,
  InterviewQuestionVideoThumbnailState,
  InterviewQuestionAIVideoModal,
  InterviewQuestionAIVideoProvider,
  InterviewQuestionVideoActionModal,
  RoleInterviewSaveAsTemplateModal,
} from "components/Missions/Roles";
import { AIRoleBuilderProvider } from "./AIRoleBuilder";
import { AI_ROLE_INTERVIEW_QUESTIONS_CONFIG, MISSION_ROLE_STATES } from "constants/index";
import { GET_MISSION_ROLE_INTERVIEW } from "graphql/queries";
import { InterviewPreview } from "components/VideoPresentation";
import { ICON_TYPE } from "components/Icon";
import { AdvancedButton, EditButton } from "components/Buttons";

/**
 * RoleInterviewQuestionsContent
 *
 * @param {Object}     missionRole
 * @param {Boolean}    canEdit      Defaults to admin-only; pass it where another role may manage the questions
 * @param {Boolean}    canGenerateWithAI  Indicates if the user can generate interview questions using AI
 */
const RoleInterviewQuestionsContent = ({
  missionRole,
  canEdit: canManage,
  canGenerateQuestionsWithAI = true,
  ...props
}) => {
  const { isAdmin, isTalentSimpleClient, canDuplicateVideoInterviews } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showAIForm, setShowAIForm] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const isRoleClosed = missionRole?.state === MISSION_ROLE_STATES.closed;
  const canEdit = (canManage ?? isAdmin) && !isRoleClosed;

  const {
    data: interviewData,
    loading: interviewLoading,
    getRowOptions,
  } = useMissionRoleInterview({
    missionRoleId: missionRole?.id,
  });
  const hasQuestions = interviewData?.questions?.length > 0;

  const {
    handleInterviewQuestionVideoUpload,
    handleInterviewIntroVideoUpload,
    handleDeleteInterviewQuestion,
    handleUpdateInterviewQuestion,
    uploadingQuestionVideo,
    handleRemoveInterview,
    removingInterview,
  } = useVideoPresentation();

  const handleEdit = (question) => {
    setSelectedQuestion(question);
    setShowAddForm(true);
  };

  const handleDelete = (question) => {
    handleDeleteInterviewQuestion({
      questionId: question.id,
      refetchQueries: [GET_MISSION_ROLE_INTERVIEW],
    });
  };

  const handleToggle = (question) => {
    handleUpdateInterviewQuestion({
      questionId: question.id,
      params: { enabled: !question.enabled },
    });
  };

  const rowOptions = (question) => getRowOptions({ question, interviewData, handleEdit, handleDelete });

  const handleChooseUploadFromDevice = async (file) => {
    if (file) {
      return handleInterviewQuestionVideoUpload(interviewData.uuid, file.questionId, file.file || file);
    }

    return null;
  };

  const questionsList = isTalentSimpleClient ? (
    <CardList
      cols={1}
      loading={interviewLoading}
      resultsPerPage={3}
      data={interviewData?.questions}
      renderer={RoleInterviewQuestionCard}
      rendererProps={{ canEdit, getOptions: rowOptions }}
      messageProps={{ title: "Missions.Roles.RoleVetting.Interviews.EmptyMsg", description: null }}
    />
  ) : (
    <DataTable
      loading={interviewLoading}
      data={interviewData?.questions}
      columns={COLUMNS(isAdmin, canEdit, handleToggle)}
      rowOptions={canEdit ? rowOptions : null}
      emptyMessage={{ title: "Missions.Roles.RoleVetting.Interviews.EmptyMsg" }}
    />
  );

  return (
    <>
      <Header {...props}>
        <H3>
          <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.Title" />
        </H3>
        {canEdit && (
          <div css={styles.actions}>
            {!interviewData?.questions && !interviewLoading && (
              <EditLink onClick={() => setShowTemplateForm(true)}>
                <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.CreateFromTemplate" />
              </EditLink>
            )}
            {hasQuestions && (
              <EditLink onClick={() => setShowAddForm(true)}>
                <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.EditButtonText" />
              </EditLink>
            )}
            {canGenerateQuestionsWithAI && (
              <EditLink onClick={() => setShowAIForm(true)} disabled={!missionRole?.jobDescription}>
                <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.CreateWithAI" />
              </EditLink>
            )}
            {hasQuestions && <InterviewPreview interviewData={interviewData} variant="link" icon={ICON_TYPE.video} />}
            <AdvancedButton
              options={[
                canDuplicateVideoInterviews && {
                  label: <FormattedMessage id="Missions.Roles.RoleInterviewQuestions.SaveAsTemplate" />,
                  icon: ICON_TYPE.edit,
                  onClick: () => setShowSaveAsTemplateModal(true),
                  disabled: !interviewData?.questions?.length,
                },
                {
                  label: <FormattedMessage id="Missions.Roles.RoleInterviewQuestions.Reset" />,
                  icon: ICON_TYPE.refresh,
                  onClick: () =>
                    handleRemoveInterview({
                      id: interviewData?.id,
                      refetchQueries: [GET_MISSION_ROLE_INTERVIEW],
                      withWarning: true,
                    }),
                  disabled: !interviewData || removingInterview,
                },
              ].filter(Boolean)}
              css={styles.advanced_button}
            />
          </div>
        )}
      </Header>
      {interviewData && (
        <div css={styles.intro_container}>
          <div css={styles.video_section}>
            {interviewData?.introVideoUrl ? (
              <>
                <H4>
                  <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.IntroVideoLabel" />
                </H4>
                <video src={interviewData.introVideoUrl} controls preload="metadata" css={styles.video_player} />
              </>
            ) : (
              <div css={styles.empty_video}>
                <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.NoIntroVideo" />
              </div>
            )}

            {isAdmin && (
              <div css={styles.pre_roll_links}>
                <FileUpload
                  accept="video/*"
                  uploadMessageId="Missions.Roles.RoleVetting.Interviews.VideoUploadButtonText"
                  onChange={(_name, file) => handleInterviewIntroVideoUpload(interviewData.uuid, file)}
                  showFilePreview={false}
                />
                {interviewData?.introVideoUrl && (
                  <DestructableLink
                    withDeleteIcon
                    onClick={() => handleInterviewIntroVideoUpload(interviewData.uuid, null)}
                  >
                    <FormattedMessage id="Global.Remove" />
                  </DestructableLink>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!canEdit || interviewLoading || hasQuestions ? (
        questionsList
      ) : (
        <EditButton onClick={() => setShowAddForm(true)}>
          <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.EditButtonText" />
        </EditButton>
      )}
      <RoleInterviewQuestionForm
        question={selectedQuestion}
        missionRole={missionRole}
        show={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setSelectedQuestion(null);
        }}
      />
      <RoleInterviewSaveAsTemplateModal
        interview={interviewData}
        missionRole={missionRole}
        show={showSaveAsTemplateModal}
        onClose={() => setShowSaveAsTemplateModal(false)}
      />
      <RoleInterviewTemplateForm
        missionRole={missionRole}
        show={showTemplateForm}
        onClose={() => setShowTemplateForm(false)}
      />
      <AIRoleBuilderProvider config={AI_ROLE_INTERVIEW_QUESTIONS_CONFIG}>
        <RoleInterviewAIQuestionsModal
          missionRole={missionRole}
          interview={interviewData}
          show={showAIForm}
          onClose={() => setShowAIForm(false)}
        />
      </AIRoleBuilderProvider>
      <InterviewQuestionAIVideoModal />
      <InterviewQuestionVideoActionModal
        onUploadFromDevice={handleChooseUploadFromDevice}
        uploadingQuestionVideo={uploadingQuestionVideo}
      />
    </>
  );
};

const COLUMNS = (isAdmin, canEdit, handleToggle) =>
  [
    isAdmin && {
      cell: (question) => (
        <Toggle
          name={`question-${question.id}`}
          value={question?.enabled}
          onChange={() => handleToggle?.(question)}
          disabled={!canEdit}
          size={TOGGLE_SIZE.small}
        />
      ),
      width: "60px",
    },
    {
      label: "Introduction",
      cell: ({ id, video, thumbnail, title, aiVideoGenerationTask, ...question }) => (
        <div css={styles.video_item}>
          <InterviewQuestionVideoThumbnailState
            question={{ id, video, thumbnail, title, aiVideoGenerationTask, ...question }}
            title={title}
            className={styles.video_thumbnail}
            canManageVideoGeneration={isAdmin}
          />
        </div>
      ),
      width: "25%",
    },
    {
      label: "Title",
      cell: ({ title }) => title,
      width: "20%",
    },
    {
      label: "Answer Length",
      cell: ({ timeLimitSeconds }) => (
        <FormattedMessage
          id="Missions.Roles.RoleVetting.Interviews.AnswerLength"
          values={{ value: timeLimitSeconds / 60 }}
        />
      ),
      width: "10%",
    },
    {
      label: "Question",
      cell: ({ description }) => description,
      width: "65%",
    },
  ].filter(Boolean);

const styles = {
  actions: css`
    display: flex;
    align-items: center;

    > * + *::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 14px;
      background: #ddd;
      margin: 0 12px;
    }
  `,
  intro_container: css`
    display: flex;
    gap: 24px;
    margin-bottom: 24px;
    align-items: flex-start;
  `,
  video_section: css`
    width: 250px;
    flex-shrink: 0;
  `,
  video_player: css`
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 8px;
    background: #000;
    margin-bottom: 8px;
  `,
  empty_video: css`
    width: 250px;
    aspect-ratio: 4 / 3;
    border-radius: 8px;
    background: #f4f4f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
    margin-bottom: 8px;
  `,
  video_thumbnail: css`
    width: 100%;
    border-radius: 6px;
    margin-bottom: 0.5rem;
  `,
  video_item: css`
    padding-top: 1.6rem;
  `,
  pre_roll_links: css`
    display: flex;
  `,
  preview_button: css`
    margin-top: auto;
    margin-bottom: 33px;
  `,
  advanced_button: css`
    height: 0;
  `,
};

RoleInterviewQuestionsContent.propTypes = {
  missionRole: PropTypes.shape({
    id: PropTypes.string.isRequired,
    jobDescription: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  canEdit: PropTypes.bool,
  canGenerateQuestionsWithAI: PropTypes.bool,
};

/**
 * RoleInterviewQuestions
 *
 * @param {Object}     missionRole
 */
const RoleInterviewQuestions = ({ missionRole, ...props }) => {
  const { data: interviewData, refetch: refetchInterview } = useMissionRoleInterview({
    missionRoleId: missionRole?.id,
  });

  return (
    <InterviewQuestionAIVideoProvider questions={interviewData?.questions || []} refetchInterview={refetchInterview}>
      <RoleInterviewQuestionsContent missionRole={missionRole} {...props} />
    </InterviewQuestionAIVideoProvider>
  );
};

RoleInterviewQuestions.propTypes = RoleInterviewQuestionsContent.propTypes;

export default RoleInterviewQuestions;
